import React, { useState } from 'react';
import { X, Undo2 } from 'lucide-react';
import { formatPrice, formatTime, formatStatus, getNextStatus } from '../../utils/formatters';
import Button from '../ui/Button';
import { notifyRefundRequestsChanged } from '../../hooks/useRefundAlerts';
import { refundRequestStatus, refundRequestKind, refundPreference } from '../../constants/statusConfig';
import logger from '../../utils/logger';
import {
  updateOrderStatus,
  resolveRefundRequest,
  sendDeliveryNotification,
  sendCompletedNotification,
} from '../../services/apiClient';

const parseItems = (lineItems) => {
  try {
    if (Array.isArray(lineItems)) return lineItems;
    if (typeof lineItems === 'string' && lineItems.length > 2) return JSON.parse(lineItems);
  } catch (err) {
    console.error('Erro ao processar itens do pedido:', err, lineItems);
  }
  return [{ name: 'Erro ao Carregar Itens', quantity: 1 }];
};

// `dimmed` esbate o cartão. Quem decide é a página: no histórico, tudo o que
// fechou há mais de uma hora. Dentro dessa hora o cartão fica a cores mesmo
// estando no histórico — é a janela em que ainda pode chegar uma reclamação, e
// é preciso encontrá-lo num relance.
//
// `alertaNovo` põe a etiqueta "Novo Pedido" a piscar: ninguém aceitou o pedido
// e a hora marcada está a menos de meia hora (ou já passou). Também é a página
// que decide, com o relógio do `useOrders` — ver `isUrgentNew`.
//
// `onRefund` pede à página que abra o modal de reembolso. O modal vive na
// página e não aqui: um reembolso total manda o cartão para o histórico, este
// componente desmonta-se, e o modal ia com ele — com a confirmação e o código
// do voucher que o staff tinha de ler ao cliente ao telefone.
const OrderItem = ({
  order, token, apiUrl, onStatusUpdate, onRefund, dimmed = false, alertaNovo = false,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const orderType = order.tipo ? order.tipo.toLowerCase() : 'pickup';
  const nextStatusInfo = getNextStatus(order.app_status, orderType);
  const statusDisplay = formatStatus(order.app_status);
  const items = parseItems(order.line_items);

  const pedidoAberto = order.refund_request_status === 'requested';
  const marcaReembolso = refundRequestStatus[order.refund_request_status];
  // Um backend anterior não manda o tipo: fica a etiqueta genérica.
  const tipoPedido = pedidoAberto
    ? refundRequestKind[order.refund_request_kind] || { label: marcaReembolso.label, icon: '↩️' }
    : null;

  const cardAccent = (() => {
    // Um pedido de reembolso ganha ao estado da cozinha. É o objetivo todo:
    // esta encomenda precisa de uma pessoa a olhar para ela agora.
    if (pedidoAberto) {
      return 'border-l-4 border-l-status-pending ring-2 ring-status-pending/30';
    }
    switch (order.app_status) {
      case 'pending':
        return 'border-l-4 border-l-status-canceled';
      case 'received':
        return 'border-l-4 border-l-accent-2';
      case 'preparing':
        return 'border-l-4 border-l-brand';
      case 'ready-to-pickup':
      case 'on-delivery':
        return 'border-l-4 border-l-status-completed';
      case 'cancelled':
        return 'border-l-4 border-l-status-canceled opacity-80';
      case 'refunded':
        return 'border-l-4 border-l-status-pending';
      default:
        return '';
    }
  })();

  const sendNotifications = async (status, orderData) => {
    const payload = {
      customer_name: orderData.customer_name,
      customer_phone: orderData.billing?.phone || orderData.customer_phone,
    };
    if (status === 'on-delivery') {
      await sendDeliveryNotification(apiUrl, token, payload);
    }
    if (status === 'completed') {
      await sendCompletedNotification(apiUrl, token, payload);
    }
  };

  const handleNextStatus = async () => {
    if (!nextStatusInfo) return;
    logger.userAction('Update order status', { orderId: order.order_id, newStatus: nextStatusInfo.next });
    setIsUpdating(true);
    try {
      const data = await updateOrderStatus(apiUrl, token, order.order_id, nextStatusInfo.next);
      if (data.status === 'success' && data.order) {
        logger.info('Order status updated successfully', { orderId: order.order_id, newStatus: nextStatusInfo.next });
        onStatusUpdate(data.order);
        await sendNotifications(nextStatusInfo.next, data.order);
      } else {
        logger.error('Unexpected response when updating order status', { data });
        alert('Erro! Formato de resposta inesperado do servidor.');
      }
    } catch (error) {
      logger.error('Failed to update order status', { orderId: order.order_id, error: error.message });
      alert(`Erro ao atualizar status: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Tem certeza que deseja CANCELAR este pedido?')) return;
    logger.userAction('Cancel order', { orderId: order.order_id });
    setIsUpdating(true);
    try {
      const data = await updateOrderStatus(apiUrl, token, order.order_id, 'cancelled');
      if (data.status === 'success' && data.order) {
        logger.info('Order cancelled successfully', { orderId: order.order_id });
        onStatusUpdate(data.order);
      } else {
        logger.error('Unexpected response when cancelling order', { data });
        alert('Erro! Formato de resposta inesperado do servidor.');
      }
    } catch (error) {
      logger.error('Failed to cancel order', { orderId: order.order_id, error: error.message });
      alert(`Erro ao cancelar pedido: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDismissRequest = async () => {
    if (!window.confirm('Dispensar o pedido de reembolso deste cliente?')) return;
    logger.userAction('Dismiss refund request', { orderId: order.order_id });
    setIsUpdating(true);
    try {
      const data = await resolveRefundRequest(apiUrl, token, order.order_id, {
        status: 'dismissed',
      });
      if (data.status === 'success' && data.order) {
        onStatusUpdate(data.order);
      }
      notifyRefundRequestsChanged();
    } catch (error) {
      logger.error('Failed to dismiss refund request', { orderId: order.order_id, error: error.message });
      alert(`Erro ao dispensar o pedido: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div
      className={`rounded-2xl bg-surface border border-border shadow-md ${cardAccent} p-5 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${
        dimmed ? 'opacity-60 hover:opacity-100' : ''
      }`}
    >
      {/* A piscar até alguém tratar do pedido. É para se ver num relance, do
          outro lado da cozinha — a cor da borda sozinha não chega. */}
      {tipoPedido && (
        <div
          className="-mt-1 mb-3 flex items-center justify-center gap-2 rounded-xl bg-status-pending px-3 py-2 text-sm font-extrabold uppercase tracking-wide text-white motion-safe:animate-alerta"
          role="status"
        >
          <span aria-hidden="true">{tipoPedido.icon}</span>
          {tipoPedido.label}
        </div>
      )}

      <div className="flex items-start justify-between border-b border-border pb-3 mb-3">
        <h3 className="text-xl font-bold text-foreground">
          Pedido #{order.order_id}
          <span
            className={`ml-2 px-2 py-1 text-xs rounded-full font-semibold ${
              orderType === 'pickup'
                ? 'bg-status-confirmed-soft text-status-confirmed'
                : 'bg-status-completed-soft text-status-completed'
            }`}
          >
            {orderType === 'pickup' ? 'PICKUP' : 'DELIVERY'}
          </span>
        </h3>
        {/* A etiqueta pisca, e não o cartão todo: o cartão inteiro a piscar
            confundia-se com o aviso de reembolso, que é outra conversa. */}
        <div
          className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold ${statusDisplay.color} ${
            alertaNovo ? 'motion-safe:animate-alerta' : ''
          }`}
        >
          {statusDisplay.icon}
          <span>{statusDisplay.label.toUpperCase()}</span>
        </div>
      </div>

      <div className="space-y-2 text-sm text-muted mb-4">
        <p>
          <span className="font-medium text-foreground">Cliente:</span> {order.customer_name}
        </p>
        <p>
          <span className="font-medium text-foreground">Telefone:</span> {order.billing?.phone || 'N/A'}
        </p>
        <p>
          <span className="font-medium text-foreground">Total:</span>{' '}
          <span className="text-lg font-extrabold text-brand">{formatPrice(order.total)}</span>
        </p>
        <p>
          <span className="font-medium text-foreground">Recebido:</span> {formatTime(order.date_created || order.date_webhook_received)}
        </p>

        {order.customer_note && order.customer_note.trim() && (
          <div className="bg-status-pending-soft px-3 py-2 rounded-lg border border-status-pending/30">
            <p className="font-medium text-status-pending text-sm mb-1">💬 Notas do Cliente:</p>
            <p className="text-foreground text-sm font-medium italic">"{order.customer_note}"</p>
          </div>
        )}

        {/* Encomenda de 0,00€: o voucher pagou tudo e não houve Stripe. Sem
            esta nota, um total a zero no cartão parece um erro — e quem está
            ao balcão pode pensar que o cliente ainda tem de pagar. */}
        {order.voucher_payment?.codes?.length > 0 && (
          <div className="bg-status-confirmed-soft px-3 py-2 rounded-lg border border-status-confirmed/30">
            <p className="font-medium text-status-confirmed text-sm mb-1">🎟️ Pago com voucher — nada a cobrar</p>
            {order.voucher_payment.codes.map((code, i) => (
              <p key={code} className="text-foreground text-sm font-mono">
                {code}
                {order.voucher_payment.coupon_ids?.[i] && (
                  <span className="text-muted"> (cupão #{order.voucher_payment.coupon_ids[i]})</span>
                )}
              </p>
            ))}
          </div>
        )}

        {marcaReembolso && (
          <div className="bg-status-pending-soft px-3 py-2 rounded-lg border border-status-pending/30">
            <p className="font-medium text-status-pending text-sm mb-1">
              {tipoPedido ? `${tipoPedido.icon} ${tipoPedido.label}` : `↩️ ${marcaReembolso.label}`}
              {order.refund_preference && (
                <span className="font-normal">
                  {' '}· o cliente prefere {refundPreference[order.refund_preference]}
                </span>
              )}
            </p>
            {order.refund_request_reason && (
              <p className="text-foreground text-sm italic whitespace-pre-line">
                "{order.refund_request_reason}"
              </p>
            )}
            {order.refund_requested_at && (
              <p className="text-muted text-xs mt-1">
                Pedido às {formatTime(order.refund_requested_at)}
              </p>
            )}
          </div>
        )}

        {orderType === 'pickup' && order.pickup_time && (
          <p className="bg-status-confirmed-soft px-2 py-1 rounded-lg border border-status-confirmed/30">
            <span className="font-medium text-status-confirmed">Horário de Levantamento:</span>
            <span className="font-bold text-foreground ml-1">{formatTime(order.pickup_time)}</span>
          </p>
        )}

        {orderType === 'delivery' && order.delivery_time && (
          <p className="bg-status-completed-soft px-2 py-1 rounded-lg border border-status-completed/30">
            <span className="font-medium text-status-completed">Horário de Entrega:</span>
            <span className="font-bold text-foreground ml-1">{formatTime(order.delivery_time)}</span>
          </p>
        )}

        {order.restaurante && (
          <p>
            <span className="font-medium text-foreground">Restaurante:</span> {order.restaurante}
          </p>
        )}
        {order.pickup_date && (
          <p>
            <span className="font-medium text-foreground">Data de Levantamento:</span> {order.pickup_date}
          </p>
        )}
        {orderType === 'delivery' && order.shipping && (
          <div className="mt-2 p-2 bg-status-completed-soft rounded-lg">
            <p className="font-medium text-foreground">Endereço de Entrega:</p>
            <p className="text-xs">{order.shipping.address_1}, {order.shipping.city}</p>
          </div>
        )}
      </div>

      <div className="mb-4">
        <p className="mb-2 font-semibold text-foreground">Itens:</p>
        <ul className="space-y-1 pl-3 text-muted">
          {items.map((item, index) => (
            <li key={index} className="flex justify-between border-l-2 border-brand/40 pl-2">
              <span className="font-medium">{item.name}</span>
              <span className="font-bold text-brand">x{item.quantity}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-col gap-2 pt-3 border-t border-border">
        {nextStatusInfo && (
          <Button onClick={handleNextStatus} loading={isUpdating} className="w-full">
            {!isUpdating && nextStatusInfo.icon}
            {nextStatusInfo.label} ({nextStatusInfo.next.toUpperCase()})
          </Button>
        )}

        {!['completed', 'cancelled', 'refunded'].includes(order.app_status) && (
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isUpdating}
            icon={X}
            className="w-full text-status-canceled"
          >
            Cancelar Pedido
          </Button>
        )}

        {/* Em todos os cartões, e não só nos sinalizados: a reclamação mais
            comum chega por telefone depois da entrega, e é aí que se devolve
            só o que falhou. */}
        <Button
          variant="ghost"
          onClick={() => onRefund?.(order)}
          disabled={isUpdating}
          icon={Undo2}
          className="w-full"
        >
          Reembolsar
        </Button>

        {pedidoAberto && (
          <Button
            variant="ghost"
            onClick={handleDismissRequest}
            disabled={isUpdating}
            className="w-full text-muted"
          >
            Dispensar pedido
          </Button>
        )}
      </div>
    </div>
  );
};

export default OrderItem;
